import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { GraduationCap, Clock, Users, BookOpen, ChevronRight, Star, Video } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API_URL}/courses`);
        setCourses(response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const handleEnroll = async (courseId) => {
    if (!user) {
      toast.error('Please login to enroll');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API_URL}/courses/${courseId}/enroll`, {}, getAuthHeaders());
      toast.success('Successfully enrolled!');
      // Refresh courses
      const response = await axios.get(`${API_URL}/courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    }
  };

  const handleJoinCourseCall = (course) => {
    if (!user) {
      toast.error('Please login to join a course session');
      navigate('/login');
      return;
    }
    // Generate room name based on course title
    const roomName = `KhalsaSidak-Course-${course.title.replace(/\s+/g, '-')}`;
    navigate(`/video-call/${roomName}`);
  };

  const getLevelColor = (level) => {
    switch (level.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-700 border-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="courses-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-saffron-100 px-4 py-2 rounded-full mb-4">
            <GraduationCap className="w-5 h-5 text-saffron-600" />
            <span className="text-saffron-700 font-medium">Sikh School</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Learn at Your Own Pace
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Master Gurmukhi, explore Sikh history, and deepen your understanding of Gurbani through our courses.
          </p>
        </div>

        {/* Featured Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: 'Total Courses', value: courses.length || '10+' },
            { label: 'Students Enrolled', value: '5,000+' },
            { label: 'Expert Teachers', value: '25+' },
            { label: 'Free Courses', value: courses.filter(c => c.price === 0).length || '5+' },
          ].map((stat, index) => (
            <Card key={index} className="text-center">
              <CardContent className="p-4">
                <p className="text-3xl font-bold text-saffron-600">{stat.value}</p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200"></div>
                <CardContent className="p-6">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : courses.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600">No courses available yet</h3>
              <p className="text-gray-500">Check back soon for new courses</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <Card 
                key={course.id} 
                className="card-hover bg-white border-gray-100 overflow-hidden"
                data-testid={`course-card-${course.id}`}
              >
                <div className="h-48 bg-gradient-to-br from-royal-600 to-royal-800 relative">
                  {course.image_url ? (
                    <img
                      src={course.image_url}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <GraduationCap className="w-16 h-16 text-white/30" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge className={getLevelColor(course.level)}>
                      {course.level}
                    </Badge>
                    {course.price === 0 && (
                      <Badge className="bg-green-600 text-white">
                        Free
                      </Badge>
                    )}
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                    {course.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {course.description}
                  </p>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {course.duration}
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {course.enrolled_count} enrolled
                    </span>
                  </div>
                  
                  <p className="text-saffron-600 text-sm font-medium mb-4">
                    By {course.instructor}
                  </p>
                  
                  {/* Modules Preview */}
                  {course.modules && course.modules.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Course Content</p>
                      <div className="space-y-1">
                        {course.modules.slice(0, 2).map((module, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                            <ChevronRight className="w-4 h-4 text-saffron-500" />
                            <span className="line-clamp-1">{module.title}</span>
                            <span className="text-gray-400 text-xs">({module.lessons} lessons)</span>
                          </div>
                        ))}
                        {course.modules.length > 2 && (
                          <p className="text-xs text-gray-400 pl-6">
                            +{course.modules.length - 2} more modules
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      {course.price > 0 ? (
                        <span className="text-2xl font-bold text-saffron-600">
                          ${course.price.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-2xl font-bold text-green-600">
                          Free
                        </span>
                      )}
                    </div>
                    <Button
                      className="bg-royal-600 hover:bg-royal-700 text-white"
                      onClick={() => handleEnroll(course.id)}
                      data-testid={`enroll-${course.id}`}
                    >
                      Enroll Now
                    </Button>
                  </div>
                  
                  {/* Join Live Session Button */}
                  <Button
                    variant="outline"
                    className="w-full mt-3 border-saffron-300 text-saffron-700 hover:bg-saffron-50"
                    onClick={() => handleJoinCourseCall(course)}
                    data-testid={`join-session-${course.id}`}
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Join Live Session
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage;
